/**
 * 交易筛选器逻辑管理
 * 处理筛选器之间的数据关系和联动逻辑
 */

import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

// 选项接口
export interface FilterOption {
    name: string
    value: string
}

// 筛选器状态接口
export interface FilterState {
    type: string // 交易类型
    time: number // 时间范围（天数）
    status: string // 状态
}

// 默认筛选配置 - 统一管理所有默认值
// 🎯 如需修改默认时间筛选，只需修改下面的 time 字段即可
// 可选值：0 | 1 | 3 | 7 | 30
export const DEFAULT_FILTER_CONFIG = {
    type: 'ALL' as string,
    time: 7 as number, // 默认时间筛选：最近7天
    status: 'All status' as string,
}

// 时间范围显示标签映射
export const TIME_DISPLAY_LABELS = {
    0: 'Today',
    1: 'Yesterday',
    3: 'Last 3 days',
    7: 'Last 7 days',
    30: 'Last 30 days',
} as const

// 获取时间筛选的显示标签
export const getTimeDisplayLabel = (timeValue: number): string => {
    return TIME_DISPLAY_LABELS[timeValue as keyof typeof TIME_DISPLAY_LABELS] || `${timeValue} days`
}

export const useTransactionFilters = () => {
    const { t } = useI18n()
    // 筛选器状态 - 使用统一的默认配置
    const selectedType = ref(DEFAULT_FILTER_CONFIG.type)
    const selectedTime = ref<number>(DEFAULT_FILTER_CONFIG.time)
    const selectedStatus = ref(DEFAULT_FILTER_CONFIG.status)

    // 弹窗显示状态
    const showTypeSheet = ref(false)
    const showTimeSheet = ref(false)
    const showStatusSheet = ref(false)

    const filterKeyValueMap: Record<string, string> = {
        ALL: t('transaction.all'),
        Deposit: t('transaction.deposit'),
        Withdraw: t('transaction.withdraw'),
        'All status': t('transaction.all_status'),
        Success: t('transaction.success'),
        Processing: t('transaction.processing'),
        Failed: t('transaction.failed'),
        Other: t('transaction.other'),
        Today: t('transaction.today'),
        Yesterday: t('transaction.yesterday'),
        'Last 3 days': t('transaction.last_days', { day: 3 }),
        'Last 7 days': t('transaction.last_days', { day: 7 }),
        'Last 30 days': t('transaction.last_days', { day: 30 }),
    }

    const getFilterTextByKey = (key: string): string | undefined => {
        return filterKeyValueMap[key]
    }

    // 第一个筛选器：交易类型
    const typeOptions: FilterOption[] = [
        { name: t('transaction.all'), value: 'ALL' },
        { name: t('transaction.deposit'), value: 'Deposit' },
        { name: t('transaction.withdraw'), value: 'Withdraw' },
    ]

    // 第二个筛选器：时间范围
    // UI显示使用标签，value使用数值便于API请求
    const timeOptions: FilterOption[] = [
        { name: t('transaction.today'), value: '0' },
        { name: t('transaction.yesterday'), value: '1' },
        { name: t('transaction.last_days', { day: 3 }), value: '3' },
        { name: t('transaction.last_days', { day: 7 }), value: '7' },
        { name: t('transaction.last_days', { day: 30 }), value: '30' },
    ]

    // 第三个筛选器：状态（动态生成）
    const statusOptions = computed(() => {
        const baseOptions = [{ name: t('transaction.all_status'), value: 'All status' }]

        switch (selectedType.value) {
            case 'ALL':
                return [...baseOptions, { name: t('transaction.success'), value: 'Success' }, { name: t('transaction.other'), value: 'Other' }]
            case 'Deposit':
                return [
                    ...baseOptions,
                    { name: t('transaction.success'), value: 'Success' },
                    { name: t('transaction.processing'), value: 'Processing' },
                ]
            case 'Withdraw':
                return [
                    ...baseOptions,
                    { name: t('transaction.success'), value: 'Success' },
                    { name: t('transaction.failed'), value: 'Failed' },
                    { name: t('transaction.processing'), value: 'Processing' },
                ]
            default:
                return baseOptions
        }
    })

    // 监听交易类型变化，重置状态筛选器
    watch(selectedType, () => {
        selectedStatus.value = 'All status'
    })

    // 筛选器点击事件
    const handleTypeFilter = () => {
        showTypeSheet.value = true
    }

    const handleTimeFilter = () => {
        showTimeSheet.value = true
    }

    const handleStatusFilter = () => {
        showStatusSheet.value = true
    }

    // 选择事件处理
    const onTypeSelect = (option: FilterOption) => {
        selectedType.value = option.value
        showTypeSheet.value = false
    }

    const onTimeSelect = (option: FilterOption) => {
        selectedTime.value = Number(option.value)
        showTimeSheet.value = false
    }

    const onStatusSelect = (option: FilterOption) => {
        selectedStatus.value = option.value
        showStatusSheet.value = false
    }

    // 获取当前筛选状态
    const getFilterState = (): FilterState => {
        return {
            type: selectedType.value,
            time: selectedTime.value,
            status: selectedStatus.value,
        }
    }

    // 重置所有筛选器 - 使用统一的默认配置
    const resetFilters = () => {
        selectedType.value = DEFAULT_FILTER_CONFIG.type
        selectedTime.value = DEFAULT_FILTER_CONFIG.time
        selectedStatus.value = DEFAULT_FILTER_CONFIG.status
    }

    return {
        // 状态
        selectedType,
        selectedTime,
        selectedStatus,

        // 弹窗状态
        showTypeSheet,
        showTimeSheet,
        showStatusSheet,

        // 选项数据
        typeOptions,
        timeOptions,
        statusOptions,

        // 事件处理
        handleTypeFilter,
        handleTimeFilter,
        handleStatusFilter,
        onTypeSelect,
        onTimeSelect,
        onStatusSelect,

        // 工具方法
        getFilterState,
        resetFilters,

        // 默认配置（供外部模块使用）
        DEFAULT_FILTER_CONFIG,

        // 工具方法
        getFilterTextByKey,
    }
}
